import { type FormEvent, useEffect, useRef, useState } from "react";
import type { Language } from "../i18n.js";
import {
  type OperatorLink,
  readRecent,
  readSavedViews,
  removeSavedView,
  saveView,
} from "../operator-memory.js";

export interface CommandDestination {
  label: string;
  search: string;
}

const COPY = {
  en: {
    close: "Close search",
    empty: "No matching screen or action.",
    go: "Go to",
    placeholder: "Screen, player, transaction or round",
    player: "player",
    recent: "Recent items",
    round: "round",
    save: "Save current view",
    saved: "Saved views",
    search: "Search",
    searchPlayer: "Search players for",
    title: "Global search",
    transaction: "transaction",
    viewName: "View name",
  },
  fr: {
    close: "Fermer la recherche",
    empty: "Aucun écran ou action correspondant.",
    go: "Aller à",
    placeholder: "Écran, joueur, transaction ou manche",
    player: "joueur",
    recent: "Éléments récents",
    round: "manche",
    save: "Enregistrer la vue actuelle",
    saved: "Vues enregistrées",
    search: "Rechercher",
    searchPlayer: "Rechercher des joueurs pour",
    title: "Recherche globale",
    transaction: "transaction",
    viewName: "Nom de la vue",
  },
} as const;

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function CommandPalette({
  adminId,
  destinations,
  language,
  onNavigate,
}: {
  adminId: string;
  destinations: CommandDestination[];
  language: Language;
  onNavigate: (search: string) => void;
}) {
  const copy = COPY[language];
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<OperatorLink[]>([]);
  const [saved, setSaved] = useState<OperatorLink[]>([]);
  const [viewName, setViewName] = useState("");

  function show() {
    setRecent(readRecent(adminId));
    setSaved(readSavedViews(adminId));
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setQuery("");
  }

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || (!event.metaKey && !event.ctrlKey)) return;
      event.preventDefault();
      setOpen((current) => !current);
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, []);

  useEffect(() => {
    if (!open) {
      dialog.current?.close?.();
      return;
    }
    setRecent(readRecent(adminId));
    setSaved(readSavedViews(adminId));
    if (dialog.current?.showModal && !dialog.current.open) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");
    input.current?.focus();
  }, [adminId, open]);

  const normalized = query.trim().toLocaleLowerCase();
  const matches = destinations.filter((item) =>
    item.label.toLocaleLowerCase().includes(normalized),
  );
  const entityActions = query.trim()
    ? [
        {
          label: `${copy.searchPlayer} “${query.trim()}”`,
          search: `?screen=players&q=${encodeURIComponent(query.trim())}`,
        },
        ...(uuid.test(query.trim())
          ? [
              {
                label: `${copy.go} ${copy.player} ${query.trim()}`,
                search: `?screen=players&player=${encodeURIComponent(query.trim())}&tab=overview`,
              },
              {
                label: `${copy.go} ${copy.transaction} ${query.trim()}`,
                search: `?screen=transactions&detail=${encodeURIComponent(query.trim())}`,
              },
              {
                label: `${copy.go} ${copy.round} ${query.trim()}`,
                search: `?screen=rounds&detail=${encodeURIComponent(query.trim())}`,
              },
            ]
          : []),
      ]
    : [];

  function go(search: string) {
    close();
    onNavigate(search);
  }

  function store(event: FormEvent) {
    event.preventDefault();
    const next = saveView(adminId, viewName, window.location.search);
    setSaved(next);
    setViewName("");
  }

  return (
    <>
      <button
        aria-haspopup="dialog"
        aria-keyshortcuts="Meta+K Control+K"
        aria-label={copy.search}
        className="command-trigger"
        onClick={show}
        type="button"
      >
        <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
          <circle cx="8.5" cy="8.5" r="4.75" stroke="currentColor" strokeWidth="1.5" />
          <path d="m12 12 4 4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
        </svg>
        <span>{copy.search}</span>
        <kbd>⌘K</kbd>
      </button>

      <dialog
        aria-labelledby="command-title"
        className="round-dialog command-dialog"
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") close();
        }}
        ref={dialog}
      >
        <div className="command-head">
          <h2 id="command-title">{copy.title}</h2>
          <button
            aria-label={copy.close}
            className="btn btn-quiet btn-sm"
            onClick={close}
            type="button"
          >
            ×
          </button>
        </div>
        <div className="command-search">
          <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
            <circle cx="8.5" cy="8.5" r="4.75" stroke="currentColor" strokeWidth="1.5" />
            <path d="m12 12 4 4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
          </svg>
          <input
            aria-label={copy.title}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={copy.placeholder}
            ref={input}
            value={query}
          />
        </div>

        <div className="command-body">
          {normalized ? (
            <section className="command-section">
              {[...matches, ...entityActions].map((item) => (
                <button
                  key={`${item.search}-${item.label}`}
                  onClick={() => go(item.search)}
                  type="button"
                >
                  <span>{item.label}</span>
                  <span aria-hidden="true">→</span>
                </button>
              ))}
              {matches.length + entityActions.length === 0 ? <p>{copy.empty}</p> : null}
            </section>
          ) : (
            <>
              <section className="command-section">
                <h3>{copy.go}</h3>
                {destinations.map((item) => (
                  <button key={item.search} onClick={() => go(item.search)} type="button">
                    <span>{item.label}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                ))}
              </section>
              {recent.length ? (
                <section className="command-section">
                  <h3>{copy.recent}</h3>
                  {recent.map((item) => (
                    <button key={item.id} onClick={() => go(item.search)} type="button">
                      <span>{item.label}</span>
                      <span aria-hidden="true">↗</span>
                    </button>
                  ))}
                </section>
              ) : null}
              <section className="command-section">
                <h3>{copy.saved}</h3>
                {saved.map((item) => (
                  <div className="command-saved" key={item.id}>
                    <button onClick={() => go(item.search)} type="button">
                      {item.label}
                    </button>
                    <button
                      aria-label={`${copy.close}: ${item.label}`}
                      onClick={() => setSaved(removeSavedView(adminId, item.id))}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <form className="command-save" onSubmit={store}>
                  <input
                    aria-label={copy.viewName}
                    onChange={(event) => setViewName(event.target.value)}
                    placeholder={copy.viewName}
                    value={viewName}
                  />
                  <button disabled={!viewName.trim()} type="submit">
                    {copy.save}
                  </button>
                </form>
              </section>
            </>
          )}
        </div>
      </dialog>
    </>
  );
}
